/**
 * ResumeInvestigationCard — "your investigation has been patiently waiting".
 * Shown near the top of the homepage / Member Desk when the reader was last
 * in the Archive more than RESUME_AFTER_MS ago. Quiet card, dismissable,
 * never a popup.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, FolderOpen } from "lucide-react";
import * as memory from "./investigationMemory";

const RESUME_AFTER_MS = 6 * 60 * 60 * 1000; // 6 hours

const relTime = (ts) => {
  const d = Date.now() - ts;
  const h = Math.floor(d / 3600000);
  if (h < 1) return "less than an hour ago";
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const days = Math.floor(h / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const ResumeInvestigationCard = ({ className = "" }) => {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(false);

  const last = memory.getLastInvestigation();
  if (hidden || !last || !last.url) return null;
  if (Date.now() - (last.ts || 0) < RESUME_AFTER_MS) return null;
  if (memory.isLastInvestigationDismissed()) return null;
  if (window.location.pathname === last.url.split("?")[0]) return null;

  const resume = () => {
    memory.resumeExternal(last.url, last.scroll);
    navigate(last.url);
  };
  const dismiss = () => {
    memory.dismissLastInvestigation();
    setHidden(true);
  };

  return (
    <div
      data-testid="resume-investigation-card"
      className={`relative border px-5 py-4 ${className}`}
      style={{ borderColor: "#3A3630", background: "#12110E" }}
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="resume-card-dismiss"
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 text-[#5A554C] hover:text-[#E4DFD3] transition-colors"
      >
        <X size={13} />
      </button>
      <div className="flex items-start gap-3">
        <FolderOpen size={15} className="text-[#A68F5E] mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#A68F5E]">
            Resume your investigation?
          </div>
          <div className="text-[12px] text-[#8A8377] mt-1">Continue where you left off</div>
          <div className="font-display text-[17px] text-[#E4DFD3] mt-1.5 truncate"
               data-testid="resume-card-title">
            {last.title || memory.titleFor(last.url)}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#5A554C] mt-1">
            Last visited: {relTime(last.ts)}
          </div>
          <button
            type="button"
            onClick={resume}
            data-testid="resume-card-btn"
            className="mt-3 inline-flex items-center gap-2 font-mono text-[9.5px] uppercase
                       tracking-[0.3em] border px-4 py-2 text-[#E4DFD3]
                       hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
            style={{ borderColor: "#3A3630" }}
          >
            Resume Investigation
          </button>
        </div>
      </div>
    </div>
  );
};
